// ** reactstrap
import { Input, InputGroup, InputGroupText, Label } from "reactstrap";

import { Controller } from "react-hook-form";
// ** Components and utils
import CustomTooltip from "@components/ui-components/tooltip";
import { DATE_PICKER_FORMAT } from "@constants/index";
// ** Third party
import DatePicker from "react-datepicker";
import ErrorBoundaryWrapper from "@views/common/error-boundary-wrapper";
import calendarSvg from "./calendar.svg";
import classnames from "classnames";
import dayjs from "dayjs";
// ** React imports
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

const SingleDatePicker = (props) => {
    const {
        control,
        name,
        errors,
        className = "",
        label = "",
        required = false,
        minDate = null,
        maxDate = null,
        id,
        format = DATE_PICKER_FORMAT,
        placeholder = "Please select a date",
        showErrMsg = true,
        customOnChange = () => {},
        showTimeSelect = false
    } = props;

    const { t } = useTranslation();
    const message = errors?.[id]?.message;
    const isInvalid = !!message;

    const CustomInput = forwardRef(({ value, onClick, onChange }, ref) => (
        <InputGroup>
            <InputGroupText
                className={classnames("input-group-merge", {
                    "border-danger": isInvalid
                })}>
                <img src={calendarSvg} alt="" />
            </InputGroupText>
            <Input
                value={value}
                onClick={onClick}
                className={classnames({ "is-invalid": isInvalid })}
                placeholder={t(placeholder)}
                onChange={onChange}
                data-cy={`input-${id}`}
                ref={ref}/>
        </InputGroup>
    ));

    const renderError = () => {
        return (
            message && (
                <small className="text-danger" data-cy={`error-${name}`}>
                    {message}
                </small>
            )
        );
    };

    const filterPassedTime = (time) => {
        const currentDate = new Date(minDate);
        const selectedDate = new Date(time);
        return currentDate.getTime() < selectedDate.getTime();
    };

    return (
        <ErrorBoundaryWrapper
            name="field"
            children={
                <div
                    className={classnames("mb-1", [className])}
                    id={`error-${name.replaceAll(".", "-")}`}>
                    {label && (
                        <Label htmlFor={name}>
                            {t(label)}
                            {required && <span className="text-danger fw-bolder">*</span>}
                        </Label>
                    )}
                    {label && <br />}
                    <Controller
                        control={control}
                        name={name}
                        render={({ field: { value, onChange } }) => (
                            <DatePicker
                                selected={value}
                                onChange={(e) => {
                                    if (!e) {
                                        onChange("");
                                        return;
                                    }
                                    if (customOnChange) {
                                        customOnChange(e);
                                    }
                                    if (filterPassedTime(e)) {
                                        onChange(e);
                                    } else {
                                        const selectedDateWithCurrentTime = dayjs(e)
                                            .set("hour", dayjs().hour())
                                            .set("minute", dayjs().minute())
                                            .set("second", dayjs().second())
                                            .toDate();
                                        onChange(selectedDateWithCurrentTime);
                                    }
                                }}
                                {...(showTimeSelect ? { filterTime: filterPassedTime, timeIntervals: 1 } : {})}
                                filterTime={filterPassedTime}
                                maxDate={maxDate}
                                customInput={<CustomInput />}
                                minDate={minDate}
                                showTimeSelect={showTimeSelect}
                                dateFormat={format || undefined}
                                showMonthDropdown
                                showYearDropdown
                                shouldCloseOnSelect
                                adjustDateOnChange
                                {...props}
                                className="form-control"/>
                        )}/>
                    {isInvalid && !showErrMsg && (
                        <CustomTooltip
                            target={`error-${name.replaceAll(".", "-")}`}
                            content={message}/>
                    )}
                    {showErrMsg && renderError()}
                </div>
            }/>
    );
};

export default SingleDatePicker;
